import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { kindOfResource, resourceLevel } from "permissions/_views/grant-row/grant-row-model";
import {
  DERIVED_FORUM_NOTE_PREFIX,
  DIRECT,
  FROM,
  contributingGrants,
  derivedForumSource,
  parentLabel,
} from "permissions/_views/wording/permission-wording";
import type Permission from "permissions/permission";
import { AccessLevel, ResourceType } from "permissions/resources/permission-resource-types";
import { WorkAgreementField } from "permissions/resources/work/work-agreement-field";

/**
 * What the person page lists for a per-record kind: one row per record the person holds a grant on.
 *
 * Plain functions, so the rules — which grant a row shows, how a field grant is summed up, which
 * document grants are legacy — read and test apart from the page. The design is
 * `docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "The grant row" and "The person page".
 */

/**
 * How many matching records one name search asks for. The ids then filter the person's grants, so
 * this bounds the id list sent to the Permissions API, not what the reader sees.
 */
export const NAME_SEARCH_LIMIT = 50;

/** One row: a record, the strongest live grant on it, and every grant that opens it. */
export type PersonRecordRow = Readonly<{
  key: string;
  /** The record's id, or the empty string for a grant that names none. */
  targetId: string;
  kind: PermissionKind;
  /** The grant the row draws: the highest live level. */
  primary: Permission;
  /** Every grant on this record, strongest first, the primary among them. More than one gets a disclosure. */
  grants: readonly Permission[];
}>;

/** The record a per-record grant is on, read from the property the kinds endpoint names. */
export function targetIdOf(kind: PermissionKind, permission: Permission): string {
  if (!kind.targetIdProperty) return "";
  const value = (permission.resource as unknown as Record<string, unknown>)[kind.targetIdProperty];
  if (value && typeof value === "object" && "value" in value) return String((value as { value: unknown }).value);
  return typeof value === "string" ? value : "";
}

/** Whether the kind's grants are per field: one proposal and one work agreement. */
export function isFieldKind(kind: PermissionKind): boolean {
  return kind.controls.some((control) => control.name === "fields");
}

export type FieldCounts = Readonly<{ granted: number; writable: number; total: number }>;

export const ALL_FIELDS: readonly WorkAgreementField[] = Object.values(WorkAgreementField);

/** The fields a per-field grant confers, and how many of them at read and write. */
export function fieldCounts(permission: Permission): FieldCounts {
  const fields = (permission.resource as { fields?: Partial<Record<WorkAgreementField, AccessLevel>> }).fields ?? {};
  const levels = ALL_FIELDS.map((field) => fields[field]).filter((level) => level !== undefined);
  return {
    granted: levels.length,
    writable: levels.filter((level) => level === AccessLevel["Read/Write"]).length,
    total: ALL_FIELDS.length,
  };
}

/**
 * How much one grant opens, to pick the row's grant: the level for a level-only kind; for a field
 * kind the read-and-write fields first, then the fields.
 */
function strength(kind: PermissionKind, permission: Permission): number {
  if (isFieldKind(kind)) {
    const counts = fieldCounts(permission);
    return counts.writable * 100 + counts.granted;
  }
  return resourceLevel(permission.resource).accessLevel ?? AccessLevel["Read-Only"];
}

/**
 * A document grant linked to a parent. Documents decide who may open a document without reading the
 * link (design decision 3), so the row is listed as legacy, with Remove, and never as "From: …".
 */
export function isLegacyDocumentLink(kind: PermissionKind, permission: Permission): boolean {
  return kind.resourceType === ResourceType.DocumentAccess && (permission.parentPermissions?.length ?? 0) > 0;
}

/**
 * One page of a kind's grants, one row per record, in the order the page lists the records. A record
 * with more than one grant — Messaging mints a derived conversation grant beside a direct one — is one
 * row drawing the strongest, with the others behind its disclosure. A grant naming no record is a row
 * of its own.
 */
export function groupRecordGrants(kind: PermissionKind, grants: readonly Permission[]): PersonRecordRow[] {
  const byTarget = new Map<string, Permission[]>();
  const order: string[] = [];
  for (const grant of grants) {
    const targetId = targetIdOf(kind, grant);
    const key = targetId ? targetId.toLowerCase() : `grant-${grant.id?.value ?? order.length}`;
    if (!byTarget.has(key)) {
      byTarget.set(key, []);
      order.push(key);
    }
    byTarget.get(key)!.push(grant);
  }

  return order.map((key) => {
    const own = [...byTarget.get(key)!].sort((left, right) => {
      if (left.isDisabled !== right.isDisabled) return left.isDisabled ? 1 : -1;
      return strength(kind, right) - strength(kind, left);
    });
    const primary = own[0];
    return { key: `record-${kind.slug}-${key}`, targetId: targetIdOf(kind, primary), kind, primary, grants: own };
  });
}

export type RecordKindCount = Readonly<{ kind: PermissionKind; count: number }>;

/**
 * The per-record kinds of one area the person holds anything of, with the server's count of their
 * grants. A kind with none is left out: a zero would be a row with nothing behind it.
 */
export function recordKindCounts(
  kinds: readonly PermissionKind[],
  area: string,
  permissionCounts: Readonly<Record<string, number>> | undefined,
): RecordKindCount[] {
  return kinds
    .filter((kind) => kind.perRecord && kind.area === area)
    .map((kind) => ({ kind, count: permissionCounts?.[kind.resourceType] ?? 0 }))
    .filter((entry) => entry.count > 0);
}

/**
 * The standing kind that caps each field of a derived proposal or work agreement grant, as the server
 * reads it in `DerivedWorkAccess.ReadProposalFieldCeilingsAsync`. The proposals or work agreements
 * grant the record came from caps every field as well.
 */
const FIELD_CEILING_TYPES: Readonly<Record<WorkAgreementField, string>> = {
  [WorkAgreementField.ClientTeam]: ResourceType.TeamsAccess,
  [WorkAgreementField.VendorTeam]: ResourceType.TeamsAccess,
  [WorkAgreementField.Policies]: ResourceType.ContractsAccess,
  [WorkAgreementField.Conflicts]: ResourceType.ContractsAccess,
  [WorkAgreementField.FeeSchedule]: ResourceType.FeeSchedulesAccess,
  [WorkAgreementField.Currency]: ResourceType.CurrencyFieldsAccess,
  [WorkAgreementField.Invoices]: ResourceType.InvoicesAccess,
  [WorkAgreementField.Discount]: ResourceType.DiscountsAccess,
};

const ENTITLING_TYPES: ReadonlySet<string> = new Set([ResourceType.ProposalsAccess, ResourceType.WorkAgreementsAccess]);

export type FieldCeiling = Readonly<{ level: AccessLevel; parent: string }>;

/**
 * The most each field of a derived grant can be, and the parent grant that set it. A field no parent
 * caps is left out: the server drops it from the derived grant rather than storing it read-only.
 */
export function fieldCeilings(
  permission: Permission,
  kinds: readonly PermissionKind[],
): Partial<Record<WorkAgreementField, FieldCeiling>> {
  const parents = permission.parentPermissions ?? [];
  const label = (parent: Permission) => {
    const kind = kindOfResource(kinds, parent.resource);
    return kind ? parentLabel(kind, resourceLevel(parent.resource).scope) : parent.resource.resourceType;
  };
  const entitling = parents.find((parent) => ENTITLING_TYPES.has(parent.resource.resourceType));
  const entitlingLevel = entitling ? resourceLevel(entitling.resource).accessLevel : undefined;

  const ceilings: Partial<Record<WorkAgreementField, FieldCeiling>> = {};
  for (const field of ALL_FIELDS) {
    const capping = parents.filter((parent) => parent.resource.resourceType === FIELD_CEILING_TYPES[field] && !parent.isDisabled);
    if (capping.length === 0) continue;
    const strongest = capping.reduce((best, parent) =>
      (resourceLevel(parent.resource).accessLevel ?? 0) > (resourceLevel(best.resource).accessLevel ?? 0) ? parent : best,
    );
    let level = resourceLevel(strongest.resource).accessLevel ?? AccessLevel["Read-Only"];
    if (entitlingLevel !== undefined && level > entitlingLevel) level = entitlingLevel;
    ceilings[field] = { level, parent: label(strongest) };
  }
  return ceilings;
}

/**
 * Where one of a record's grants came from, as one part of the row's disclosure: "Direct", "from
 * communications (whole organization)", or "from taking part in a proposal".
 */
export function grantOrigin(permission: Permission, kinds: readonly PermissionKind[]): string {
  const parents = permission.parentPermissions ?? [];
  if (parents.length > 0 && permission.resource.resourceType !== ResourceType.DocumentAccess) {
    const labels = parents.map((parent) => {
      const kind = kindOfResource(kinds, parent.resource);
      return kind ? parentLabel(kind, resourceLevel(parent.resource).scope) : parent.resource.resourceType;
    });
    return `from ${labels.join(" + ")}`;
  }
  if (permission.note?.startsWith(DERIVED_FORUM_NOTE_PREFIX)) {
    return `from ${derivedForumSource(permission.note).slice(FROM.length).trim()}`;
  }
  return DIRECT;
}

/** The disclosure on a row more than one grant opens: "Direct + from communications (2 grants)". */
export function disclosureLabel(row: PersonRecordRow, kinds: readonly PermissionKind[]): string | undefined {
  if (row.grants.length < 2) return undefined;
  return contributingGrants(row.grants.map((grant) => grantOrigin(grant, kinds)));
}
